import { useEffect } from "react";

const REVEAL_SELECTOR = ".jh-feature-card, .jh-step, .jh-offering-card, .jh-stat";
const NEAR_VIEWPORT_MARGIN = 80;
const SAFETY_DELAY_MS = 2200;

export function useScrollReveal(rootSelector) {
  useEffect(() => {
    const root = document.querySelector(rootSelector);
    if (!root) return;
    root.classList.add("jh-anim");

    const reveal = (el) => el.classList.add("jh-in-view");

    const nearViewport = (el) => {
      const rect = el.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      return (
        rect.top < viewportHeight + NEAR_VIEWPORT_MARGIN &&
        rect.bottom > -NEAR_VIEWPORT_MARGIN
      );
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -48px 0px" },
    );

    const observe = (el) => {
      if (el.dataset.jhObserved) return;
      el.dataset.jhObserved = "1";
      if (nearViewport(el)) reveal(el);
      else observer.observe(el);
    };

    const scan = (node) => {
      if (node.nodeType !== 1) return;
      if (node.matches?.(REVEAL_SELECTOR)) observe(node);
      node.querySelectorAll?.(REVEAL_SELECTOR).forEach(observe);
    };
    scan(root);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach(scan));
    });
    mutationObserver.observe(root, { childList: true, subtree: true });

    const safetyTimer = setTimeout(() => {
      root.querySelectorAll(REVEAL_SELECTOR).forEach(reveal);
    }, SAFETY_DELAY_MS);

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      clearTimeout(safetyTimer);
    };
  }, [rootSelector]);
}
